import { Link } from "@tanstack/react-router";
import { useT } from "../../i18n";
import { Zeichen, type Zeichenname } from "../../bausteine/Zeichen";
import "../../stile/handy-leiste.css";

/* ══════════════════════════════════════════════════════════════════
   Eine Liste von Wegen — Zeichen, Wort, Satz, Winkel.

   Sie steht an zwei Stellen: unter „Mehr" und auf dem Startschirm des
   Verkaufs. Zwei Listen mit demselben Zweck und zwei Formen wären die
   sichere Art, beim dritten Punkt auseinanderzulaufen.

   **Ein Eintrag ist entweder ein Weg oder eine Handlung.** Der Weg
   wechselt die Maske und ist ein `Link` — damit geht Mittelklick, und
   der Router holt die Maske schon beim Überfahren. Die Handlung öffnet
   ein Blatt und ist ein `<button>`: Sie tut etwas, sie geht nirgendwo
   hin. Den Unterschied sieht man am Winkel rechts, den nur der Weg
   trägt.
   ══════════════════════════════════════════════════════════════════ */

export type Wegeintrag = {
  /** Der i18n-Schlüssel des Worts. */
  text: string;
  /** Der Satz darunter — freiwillig. */
  satz?: string | undefined;
  /**
   * Das Zeichen links. Freiwillig seit dem 17.09.2026: Die Punkte der
   * Seitenleiste stehen unter „Mehr" mit, und die haben am Schreibtisch
   * bewusst keine Symbole (28-designkonzept.md, „Navigation"). Ein Zeichen
   * für sie zu erfinden hieße, sie am Telefon anders zu nennen als dort.
   */
  zeichen?: Zeichenname | undefined;
} & ({ weg: string; tun?: undefined } | { weg?: undefined; tun: () => void });

function Inhalt({ e }: { e: Wegeintrag }) {
  const t = useT();
  return (
    <>
      {e.zeichen ? <Zeichen name={e.zeichen} /> : null}
      <b>{t(e.text)}</b>
      {e.satz ? <small>{t(e.satz)}</small> : null}
      {e.weg ? <Zeichen name="weiter" /> : null}
    </>
  );
}

/** Ohne Zeichen rückt die Zeile an den Rand, statt eine leere Spalte zu halten. */
const wegklasse = (e: Wegeintrag) => (e.zeichen ? "hweg-zeile" : "hweg-zeile ohne-zeichen");

export function Wegeliste({ eintraege }: { eintraege: Wegeintrag[] }) {
  if (!eintraege.length) return null;
  return (
    <div className="hwege">
      {eintraege.map((e) => (
        e.weg !== undefined ? (
          <Link key={e.text} className={wegklasse(e)} to={e.weg}>
            <Inhalt e={e} />
          </Link>
        ) : (
          <button key={e.text} type="button" className={wegklasse(e)} onClick={e.tun}>
            <Inhalt e={e} />
          </button>
        )
      ))}
    </div>
  );
}
